/**
 * FileUploadZone — reusable paste / click-to-add / drag-drop file attachment component.
 *
 * Props:
 *   files        — array of { id, filename, mime_type, file_size, status, file? }
 *   onAdd        — (fileList) => void   called with raw File objects
 *   onRemove     — (id) => void
 *   disabled     — bool
 *   maxFiles     — int (default 10)
 *   maxSizeMB    — int (default 20)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const ACCEPT_TYPES = [
  'image/png','image/jpeg','image/gif','image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv','text/plain',
];

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mime) {
  if (!mime) return '📄';
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf') return '📕';
  if (mime.includes('word')) return '📝';
  if (mime.includes('excel') || mime === 'text/csv') return '📊';
  return '📄';
}

export default function FileUploadZone({
  files = [],
  onAdd,
  onRemove,
  disabled = false,
  maxFiles = 10,
  maxSizeMB = 20,
}) {
  const inputRef   = useRef(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState('');

  const validate = useCallback((rawFiles) => {
    setError('');
    const remaining = maxFiles - files.length;
    if (rawFiles.length > remaining) {
      setError(`Max ${maxFiles} files allowed.`);
      return [];
    }
    const maxBytes = maxSizeMB * 1024 * 1024;
    const valid = [];
    for (const f of rawFiles) {
      if (f.size > maxBytes) {
        setError(`"${f.name}" exceeds ${maxSizeMB} MB limit.`);
        continue;
      }
      valid.push(f);
    }
    return valid;
  }, [files.length, maxFiles, maxSizeMB]);

  const handleFiles = useCallback((rawFiles) => {
    const valid = validate(Array.from(rawFiles));
    if (valid.length) onAdd(valid);
  }, [validate, onAdd]);

  // ── Paste from clipboard ──────────────────────────────────────────────────
  useEffect(() => {
    if (disabled) return;
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pasted = [];
      for (const item of items) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f) pasted.push(f);
        }
      }
      if (pasted.length) handleFiles(pasted);
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
  }, [disabled, handleFiles]);

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const onDragOver  = (e) => { e.preventDefault(); if (!disabled) setDrag(true);  };
  const onDragLeave = ()  => setDrag(false);
  const onDrop      = (e) => {
    e.preventDefault();
    setDrag(false);
    if (!disabled) handleFiles(e.dataTransfer.files);
  };

  const canAdd = !disabled && files.length < maxFiles;

  return (
    <div>
      {/* Drop zone / prompt */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => canAdd && inputRef.current?.click()}
        style={{
          border: `1.5px dashed ${drag ? 'var(--ink)' : 'var(--paper-3)'}`,
          borderRadius: 'var(--r)',
          padding: '14px 16px',
          background: drag ? 'var(--paper-2)' : 'var(--paper)',
          cursor: canAdd ? 'pointer' : 'default',
          transition: 'all 0.15s',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <span style={{ fontSize: '18px' }}>📎</span>
        <div>
          <p style={{ fontSize: '13px', color: 'var(--ink)', margin: 0 }}>
            {canAdd
              ? 'Click to add files, drag & drop, or paste from clipboard'
              : `${maxFiles} file limit reached`}
          </p>
          <p style={{ fontSize: '11px', color: 'var(--ink-mute)', margin: '2px 0 0' }}>
            Images, PDF, Word, Excel, CSV — max {maxSizeMB} MB each
          </p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
      />

      {error && (
        <p style={{ fontSize: '12px', color: '#c03030', marginTop: '6px' }}>{error}</p>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {files.map(f => (
            <FileRow
              key={f.id}
              file={f}
              onRemove={() => onRemove(f.id)}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FileRow({ file, onRemove, disabled }) {
  const isUploading = file.status === 'uploading';
  const isError     = file.status === 'error';
  const isDone      = file.status === 'done';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '8px 12px', borderRadius: 'var(--r)',
      background: isError ? 'rgba(224,112,112,0.07)' : 'var(--white)',
      border: `1px solid ${isError ? 'rgba(224,112,112,0.3)' : 'var(--paper-3)'}`,
    }}>
      <span style={{ fontSize: '16px', flexShrink: 0 }}>{fileIcon(file.mime_type)}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontSize: '13px', fontWeight: '500', margin: 0,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {file.filename}
        </p>
        <p style={{ fontSize: '11px', color: 'var(--ink-mute)', margin: '1px 0 0' }}>
          {isUploading && 'Uploading…'}
          {isError     && <span style={{ color: '#c03030' }}>Upload failed — will retry on save</span>}
          {isDone      && formatBytes(file.file_size)}
          {!isUploading && !isError && !isDone && formatBytes(file.file_size)}
        </p>

        {/* Progress bar */}
        {isUploading && (
          <div style={{
            marginTop: '4px', height: '2px', borderRadius: '2px',
            background: 'var(--paper-3)', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', borderRadius: '2px',
              background: 'var(--ink)',
              animation: 'pulse-width 1.5s ease-in-out infinite',
              width: '60%',
            }} />
          </div>
        )}
      </div>

      {isDone && (
        <span style={{ fontSize: '13px', color: '#2d8a6e', flexShrink: 0 }}>✓</span>
      )}

      {!disabled && (
        <button
          onClick={onRemove}
          title="Remove file"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '2px 4px', borderRadius: '4px', flexShrink: 0,
            fontSize: '14px', color: 'var(--ink-mute)', lineHeight: 1,
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#c03030'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-mute)'}
        >
          ✕
        </button>
      )}
    </div>
  );
}
